/**
 * AES-GCM-256 encryption with PBKDF2-SHA256 key derivation using the
 * browser's Web Crypto API. The 600k iterations default mirrors current
 * OWASP guidance for SHA-256 (2023+). Tests may override `iterations`
 * for speed; production callers should not.
 */

export const PRODUCTION_ITERATIONS = 600_000
export const SALT_BYTES = 16
export const IV_BYTES = 12

export const ALGORITHM_LABEL = 'AES-GCM-256' as const
export const KDF_LABEL = 'PBKDF2-SHA256-600k' as const

export interface EncryptedEnvelope {
  algorithm: typeof ALGORITHM_LABEL
  kdf: typeof KDF_LABEL
  salt: string
  iv: string
  ciphertext: string
}

export interface CryptoOptions {
  /** Override the PBKDF2 iteration count. Test-only — production stays at 600k. */
  iterations?: number
}

export async function encryptString(
  plaintext: string,
  password: string,
  options: CryptoOptions = {},
): Promise<EncryptedEnvelope> {
  const iterations = options.iterations ?? PRODUCTION_ITERATIONS
  const salt = crypto.getRandomValues(new Uint8Array(SALT_BYTES))
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES))
  const key = await deriveKey(password, salt, iterations)
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: iv as BufferSource },
    key,
    new TextEncoder().encode(plaintext) as BufferSource,
  )
  return {
    algorithm: ALGORITHM_LABEL,
    kdf: KDF_LABEL,
    salt: bytesToBase64(salt),
    iv: bytesToBase64(iv),
    ciphertext: bytesToBase64(new Uint8Array(ciphertext)),
  }
}

export async function deriveKey(
  password: string,
  salt: Uint8Array,
  iterations: number,
): Promise<CryptoKey> {
  const passwordKey = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(password) as BufferSource,
    { name: 'PBKDF2' },
    false,
    ['deriveKey'],
  )
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: salt as BufferSource, iterations, hash: 'SHA-256' },
    passwordKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  )
}

export function bytesToBase64(bytes: Uint8Array): string {
  let binary = ''
  for (const byte of bytes) {
    binary += String.fromCharCode(byte)
  }
  return btoa(binary)
}

export function base64ToBytes(value: string): Uint8Array {
  const binary = atob(value)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes
}
